// pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import Header from '../components/layout/Header';
import NavigationButton from '../components/layout/Navigation';
import PWAInstallGuideModal from '../components/common/PWAInstallGuideModal';
import authService from '../services/auth';

import businessProfileService from '../services/businessProfile';
import Pagination from '../components/common/Pagination';
import '../styles/Body.css';

const formatDate = (dateData) => {
    if (!dateData) return 'tidak ada';
    const date = new Date(dateData);
    return date.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const formatIDR = (amount) => {
    return new Intl.NumberFormat('id-ID', {
        minimumFractionDigits: 0,
    }).format(amount);
};

const GENDER_CHOICES = {
    'l': 'Laki-laki',
    'p': 'Perempuan',
};

const AGAMA_CHOICES = {
    'islam': 'Islam',
    'kristen': 'Kristen',
    'katolik': 'Katolik',
    'hindu': 'Hindu',
    'buddha': 'Buddha',
    'konghucu': 'Konghucu',
    'kepercayaan': 'Kepercayaan YME / Lainnya',
};

const MARITAL_CHOICES = {
    'bn': 'Belum Nikah',
    'n': 'Nikah',
    'd': 'Duda',
    'j': 'Janda',
};

const SEGMENT_CHOICES = {
    'mahasiswa': 'Mahasiswa',
    'pelajar': 'Pelajar',
    'santri': 'Santri',
    'karyawan': 'Karyawan',
    'umum': 'Umum',
};

const STUDY_LEVEL_CHOICES = {
    'sd': 'Sekolah Dasar atau Setara',
    'smp': 'Sekolah Menengah Pertama atau Setara',
    'sma': 'Sekolah Menengah Atas / Kejuruan atau Setara',
    's1': 'Sarjana',
    's2': 'Magister',
    's3': 'Doktor',
};

const JOB_CHOICES = {
    'mahasiswa': 'Mahasiswa',
    'asn': 'Aparatur Sipil Negara',
    'karyawan_swasta': 'Karyawan Swasta',
    'guru': 'Guru',
    'dosen': 'Dosen',
    'dokter': 'Dokter',
    'perawat': 'Perawat',
    'apoteker': 'Apoteker',
    'programmer': 'Programmer',
    'data_scientist': 'Data Scientist',
    'desainer_grafis': 'Desainer Grafis',
    'marketing': 'Marketing',
    'hrd': 'HRD (Human Resources Department)',
    'akuntan': 'Akuntan',
    'konsultan': 'Konsultan',
    'arsitek': 'Arsitek',
    'insinyur': 'Insinyur',
    'peneliti': 'Peneliti',
    'jurnalis': 'Jurnalis',
    'penulis': 'Penulis',
    'penerjemah': 'Penerjemah',
    'pilot': 'Pilot',
    'pramugari': 'Pramugari',
    'chef': 'Chef',
    'pengusaha': 'Pengusaha',
    'petani': 'Petani',
    'nelayan': 'Nelayan',
    'pengrajin': 'Pengrajin',
    'teknisi': 'Teknisi',
    'seniman': 'Seniman',
    'musisi': 'Musisi',
    'atlet': 'Atlet',
    'polisi': 'Polisi',
    'tentara': 'Tentara',
    'pengacara': 'Pengacara',
    'notaris': 'Notaris',
    'psikolog': 'Psikolog',
    'sopir': 'Sopir',
    'kurir': 'Kurir',
    'barista': 'Barista',
    'freelancer': 'Freelancer',
};

const WORK_FIELD_CHOICES = {
    'pendidikan': 'Pendidikan',
    'kesehatan': 'Kesehatan',
    'ekobis': 'Ekonomi Bisnis',
    'agrotek': 'Agrotek',
    'herbal': 'Herbal-Farmasi',
    'it': 'IT',
    'manufaktur': 'Manufaktur',
    'energi': 'Energi-Mineral',
    'sains': 'Sains',
    'teknologi': 'Teknologi',
    'polhuk': 'Politik-Hukum',
    'humaniora': 'Humaniora',
    'media': 'Media-Literasi',
    'sejarah': 'Sejarah',
};

const PROVINCE_CHOICES = {
    'aceh': 'Aceh',
    'sumatera_utara': 'Sumatera Utara',
    'sumatera_barat': 'Sumatera Barat',
    'riau': 'Riau',
    'jambi': 'Jambi',
    'sumatera_selatan': 'Sumatera Selatan',
    'bengkulu': 'Bengkulu',
    'lampung': 'Lampung',
    'kepulauan_bangka_belitung': 'Kepulauan Bangka Belitung',
    'kepulauan_riau': 'Kepulauan Riau',
    'dki_jakarta': 'DKI Jakarta',
    'jawa_barat': 'Jawa Barat',
    'jawa_tengah': 'Jawa Tengah',
    'di_yogyakarta': 'DI Yogyakarta',
    'jawa_timur': 'Jawa Timur',
    'banten': 'Banten',
    'bali': 'Bali',
    'nusa_tenggara_barat': 'Nusa Tenggara Barat',
    'nusa_tenggara_timur': 'Nusa Tenggara Timur',
    'kalimantan_barat': 'Kalimantan Barat',
    'kalimantan_tengah': 'Kalimantan Tengah',
    'kalimantan_selatan': 'Kalimantan Selatan',
    'kalimantan_timur': 'Kalimantan Timur',
    'kalimantan_utara': 'Kalimantan Utara',
    'sulawesi_utara': 'Sulawesi Utara',
    'sulawesi_tengah': 'Sulawesi Tengah',
    'sulawesi_selatan': 'Sulawesi Selatan',
    'sulawesi_tenggara': 'Sulawesi Tenggara',
    'gorontalo': 'Gorontalo',
    'sulawesi_barat': 'Sulawesi Barat',
    'maluku': 'Maluku',
    'maluku_utara': 'Maluku Utara',
    'papua': 'Papua',
    'papua_barat': 'Papua Barat',
};

const ProfileInfoItem = ({ label, value, icon, fullWidth = false }) => (
    <div className={`p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-start gap-3 ${fullWidth ? 'col-span-full' : ''}`}>
        <div className="w-10 h-10 bg-white rounded-xl shadow-sm border border-gray-50 flex items-center justify-center shrink-0">
            <span className="material-icons text-green-600 text-lg">{icon}</span>
        </div>
        <div className="flex-1 overflow-hidden">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{label}</label>
            <p className="text-sm font-bold text-gray-800 line-clamp-2 leading-tight">
                {value || <span className="text-gray-300 font-normal italic">belum diisi</span>}
            </p>
        </div>
    </div>
);

const CoursesTab = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        const fetchCourses = async () => {
            const user = JSON.parse(localStorage.getItem('user'));
            if (!user) return;
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/courses/enrollments/`, {
                    headers: { Authorization: `Bearer ${user.access}` }
                });
                const verified = res.data.filter(e => e.payment_status === 'verified' || e.payment_status === 'paid');
                // Urutan paling atas adalah yang terbaru
                const sorted = verified.sort((a, b) => new Date(b.enrolled_at || b.id || 0) - new Date(a.enrolled_at || a.id || 0));
                setCourses(sorted);
            } catch (err) {
                console.error('Error fetching courses:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();
    }, []);

    const CourseSkeleton = () => (
        <div className="space-y-4 animate-pulse">
            <h2 className="font-bold text-gray-800 w-32 h-5 bg-gray-200 rounded mb-3"></h2>
            <div className="grid grid-cols-1 gap-3">
                {[1, 2].map(i => (
                    <div key={i} className="bg-white p-3 rounded-xl border shadow-sm flex items-center gap-3">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                            <div className="flex gap-2 mb-2">
                                <div className="h-4 bg-gray-100 rounded w-16"></div>
                                <div className="h-4 bg-gray-100 rounded w-16"></div>
                            </div>
                            <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                        </div>
                        <div className="w-16 h-8 bg-gray-200 rounded-full"></div>
                    </div>
                ))}
            </div>
        </div>
    );

    if (loading) return <CourseSkeleton />;

    const paginatedCourses = courses.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
                <h2 className="font-bold text-gray-800">E-Course Saya</h2>
                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-bold">
                    {courses.length} Kelas
                </span>
            </div>
            {courses.length === 0 ? (
                <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl border border-dashed">
                    <p className="text-sm">Belum ada e-course yang diikuti</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3">
                        {paginatedCourses.map(enroll => (
                            <div key={enroll.id} className="bg-white p-3 rounded-xl border shadow-sm flex items-center gap-3 hover:shadow-md transition">
                                <div className="w-16 h-16 bg-green-50 rounded-xl overflow-hidden flex items-center justify-center shrink-0 border border-gray-100">
                                    {enroll.course_thumbnail ? (
                                        <img 
                                            src={enroll.course_thumbnail.startsWith('http') ? enroll.course_thumbnail : `${process.env.REACT_APP_API_BASE_URL}${enroll.course_thumbnail}`} 
                                            alt={enroll.course_title} 
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                        />
                                    ) : null}
                                    <span className={`material-icons text-green-600 ${enroll.course_thumbnail ? 'hidden' : ''}`}>school</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-bold text-gray-800">{enroll.course_title}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                            <span className="material-icons text-[10px]">person</span>
                                            {enroll.student_count || 0} Siswa
                                        </span>
                                        <span className="text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                            <span className="material-icons text-[10px]">video_library</span>
                                            {enroll.material_count || 0} Materi
                                        </span>
                                    </div>
                                    <p className="text-[9px] text-gray-400 mt-1">Terdaftar: {formatDate(enroll.enrolled_at)}</p>
                                </div>
                                <Link
                                    to={`/kelas/buka/${enroll.course_slug}`}
                                    className="bg-green-600 text-white px-4 py-1.5 rounded-full text-xs font-bold shrink-0 hover:bg-green-700 transition shadow-sm"
                                >
                                    Buka
                                </Link>
                            </div>
                        ))}
                    </div>
                    <Pagination 
                        currentPage={currentPage}
                        totalItems={courses.length}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </div>
    );
};

const PurchasesTab = () => {
    const [purchases, setPurchases] = useState([]);
    const [physicalOrders, setPhysicalOrders] = useState([]);
    const [unreviewedCount, setUnreviewedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [historyType, setHistoryType] = useState('sinergy'); // 'sinergy' (Toko) or 'digital'
    const [statusFilter, setStatusFilter] = useState('all');
    const [digitalPage, setDigitalPage] = useState(1);
    const [sinergyPage, setSinergyPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        const fetchAllHistory = async () => {
            const user = JSON.parse(localStorage.getItem('user'));
            if (!user) return;
            setLoading(true);
            try {
                // Fetch Digital & sort newest first
                const resDigital = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/digital-products/orders/my-purchases/`, {
                    headers: { Authorization: `Bearer ${user.access}` }
                }).catch(() => ({ data: [] }));
                const sortedDigital = (resDigital.data || []).sort((a, b) => new Date(b.created_at || b.id || 0) - new Date(a.created_at || a.id || 0));
                setPurchases(sortedDigital);

                // Fetch Store / Physical Orders & sort newest first
                const resSinergy = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/orders/`, {
                    headers: { Authorization: `Bearer ${user.access}` }
                }).catch(() => ({ data: [] }));
                const sortedSinergy = (resSinergy.data || []).sort((a, b) => new Date(b.created_at || b.id || 0) - new Date(a.created_at || a.id || 0));
                setPhysicalOrders(sortedSinergy);

                // Fetch unreviewed count
                const resUnreviewed = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/orders/unreviewed-products/`, {
                    headers: { Authorization: `Bearer ${user.access}` }
                }).catch(() => ({ data: [] }));
                setUnreviewedCount((resUnreviewed.data || []).length);
            } catch (err) {
                console.error('Error fetching purchase history:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAllHistory();
    }, []);

    // Filter counts for Store
    const pendingCount = physicalOrders.filter(o => ['pending', 'menunggu', 'unpaid'].includes((o.status || '').toLowerCase())).length;
    const processCount = physicalOrders.filter(o => ['paid', 'proses', 'diproses', 'dibayar'].includes((o.status || '').toLowerCase())).length;
    const shippingCount = physicalOrders.filter(o => ['shipped', 'dikirim', 'dalam pengiriman'].includes((o.status || '').toLowerCase())).length;
    const completedCount = physicalOrders.filter(o => ['selesai', 'completed', 'delivered'].includes((o.status || '').toLowerCase())).length;
    const cancelledCount = physicalOrders.filter(o => ['batal', 'cancelled', 'failed', 'gagal'].includes((o.status || '').toLowerCase())).length;
    const activeStoreActionCount = pendingCount + shippingCount + (unreviewedCount > 0 ? 1 : 0);

    // Filter physical orders based on selected filter
    const filteredPhysicalOrders = physicalOrders.filter(order => {
        if (statusFilter === 'all') return true;
        const status = (order.status || '').toLowerCase();
        if (statusFilter === 'pending') return ['pending', 'menunggu', 'unpaid'].includes(status);
        if (statusFilter === 'processing') return ['paid', 'proses', 'diproses', 'dibayar'].includes(status);
        if (statusFilter === 'shipping') return ['shipped', 'dikirim', 'dalam pengiriman'].includes(status);
        if (statusFilter === 'completed') return ['selesai', 'completed', 'delivered'].includes(status);
        if (statusFilter === 'cancelled') return ['batal', 'cancelled', 'failed', 'gagal'].includes(status);
        return true;
    });

    const PurchaseSkeleton = () => (
        <div className="space-y-4 animate-pulse">
            <div className="flex gap-2 mb-4">
                <div className="h-8 bg-gray-200 rounded-lg w-24"></div>
                <div className="h-8 bg-gray-200 rounded-lg w-24"></div>
            </div>
            <div className="space-y-3">
                {[1, 2].map(i => (
                    <div key={i} className="bg-white p-3 rounded-xl border shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                            <div className="h-4 bg-gray-200 rounded-full w-16"></div>
                        </div>
                        <div className="flex justify-between items-center mb-3">
                            <div className="h-3 bg-gray-100 rounded w-24"></div>
                            <div className="h-3 bg-gray-100 rounded w-20"></div>
                        </div>
                        <div className="mt-3 pt-2 border-t flex justify-end">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    if (loading) return <PurchaseSkeleton />;

    const paginatedDigital = purchases.slice((digitalPage - 1) * ITEMS_PER_PAGE, digitalPage * ITEMS_PER_PAGE);
    const paginatedPhysical = filteredPhysicalOrders.slice((sinergyPage - 1) * ITEMS_PER_PAGE, sinergyPage * ITEMS_PER_PAGE);

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Tab Type Switcher */}
            <div className="flex items-center justify-between">
                <h2 className="font-bold text-gray-800">Riwayat Transaksi</h2>
                <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
                    <button
                        onClick={() => { setHistoryType('sinergy'); setSinergyPage(1); }}
                        className={`relative px-3.5 py-1.5 rounded-lg text-[11px] font-black uppercase transition-all flex items-center gap-1.5 ${
                            historyType === 'sinergy' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                        }`}
                    >
                        <span>Toko ({physicalOrders.length})</span>
                        {activeStoreActionCount > 0 && (
                            <span className="flex h-2 w-2 relative">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => { setHistoryType('digital'); setDigitalPage(1); }}
                        className={`px-3.5 py-1.5 rounded-lg text-[11px] font-black uppercase transition-all ${
                            historyType === 'digital' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                        }`}
                    >
                        Digital ({purchases.length})
                    </button>
                </div>
            </div>

            {historyType === 'digital' ? (
                purchases.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-2xl border border-dashed text-xs">
                        <span className="material-icons text-3xl mb-1 text-gray-300">inventory_2</span>
                        <p>Belum ada riwayat pembelian produk digital</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            {paginatedDigital.map(order => {
                                const rawPrice = Number(order.amount) || Number(order.product_price) || Number(order.price) || 0;
                                const title = order.product_title || 'Produk Digital';
                                const slug = order.product_slug || order.digital_product_slug || (typeof order.digital_product === 'string' ? order.digital_product : order.digital_product?.slug) || '';
                                const detailUrl = slug ? `/digital-products/${slug}` : '/digital-products';

                                return (
                                    <div key={order.id} className="bg-white p-3.5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="text-sm font-bold text-gray-800">{title}</h3>
                                            <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-bold border border-emerald-100">
                                                {order.payment_status === 'verified' ? 'Terverifikasi' : 'Menunggu'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-[10px] text-gray-400">
                                            <span>#{order.order_number || `DIG-${order.id}`}</span>
                                            <span>{formatDate(order.created_at)}</span>
                                        </div>
                                        <div className="mt-3 pt-2.5 border-t border-gray-50 flex justify-between items-center">
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase">Harga Produk</p>
                                                <span className="text-xs font-black text-gray-900">Rp {formatIDR(rawPrice)}</span>
                                            </div>
                                            <Link to={detailUrl} className="text-[11px] bg-green-50 text-green-700 hover:bg-green-100 px-3 py-1.5 rounded-xl font-bold transition flex items-center gap-1">
                                                <span>Detail Produk</span>
                                                <span className="material-icons text-xs">arrow_forward</span>
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Pagination 
                            currentPage={digitalPage}
                            totalItems={purchases.length}
                            itemsPerPage={ITEMS_PER_PAGE}
                            onPageChange={setDigitalPage}
                        />
                    </>
                )
            ) : (
                <>
                    {/* Status Filter Horizontal Pills for Store */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-2 pt-1 scrollbar-none">
                        <button
                            onClick={() => { setStatusFilter('all'); setSinergyPage(1); }}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                statusFilter === 'all'
                                    ? 'bg-gray-900 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <span>Semua</span>
                            <span className="text-[10px] opacity-75 font-normal">({physicalOrders.length})</span>
                        </button>

                        <button
                            onClick={() => { setStatusFilter('pending'); setSinergyPage(1); }}
                            className={`relative px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                statusFilter === 'pending'
                                    ? 'bg-amber-600 text-white shadow-sm'
                                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/60'
                            }`}
                        >
                            <span>Menunggu Bayar</span>
                            {pendingCount > 0 && (
                                <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-black ${statusFilter === 'pending' ? 'bg-white text-amber-700' : 'bg-red-500 text-white'}`}>
                                    {pendingCount}
                                </span>
                            )}
                        </button>

                        <button
                            onClick={() => { setStatusFilter('processing'); setSinergyPage(1); }}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                statusFilter === 'processing'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/60'
                            }`}
                        >
                            <span>Diproses</span>
                            {processCount > 0 && (
                                <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-black ${statusFilter === 'processing' ? 'bg-white text-blue-700' : 'bg-blue-200 text-blue-800'}`}>
                                    {processCount}
                                </span>
                            )}
                        </button>

                        <button
                            onClick={() => { setStatusFilter('shipping'); setSinergyPage(1); }}
                            className={`relative px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                statusFilter === 'shipping'
                                    ? 'bg-purple-600 text-white shadow-sm'
                                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200/60'
                            }`}
                        >
                            <span>Dikirim</span>
                            {shippingCount > 0 && (
                                <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-black ${statusFilter === 'shipping' ? 'bg-white text-purple-700' : 'bg-red-500 text-white'}`}>
                                    {shippingCount}
                                </span>
                            )}
                        </button>

                        <button
                            onClick={() => { setStatusFilter('completed'); setSinergyPage(1); }}
                            className={`relative px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                statusFilter === 'completed'
                                    ? 'bg-emerald-600 text-white shadow-sm'
                                    : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/60'
                            }`}
                        >
                            <span>Selesai</span>
                            {unreviewedCount > 0 && (
                                <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-black ${statusFilter === 'completed' ? 'bg-white text-emerald-700' : 'bg-amber-500 text-white'}`} title="Butuh Testimoni">
                                    {unreviewedCount} Ulasan
                                </span>
                            )}
                        </button>

                        <button
                            onClick={() => { setStatusFilter('cancelled'); setSinergyPage(1); }}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                statusFilter === 'cancelled'
                                    ? 'bg-red-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                        >
                            <span>Batal</span>
                            {cancelledCount > 0 && (
                                <span className="text-[10px] opacity-75 font-normal">({cancelledCount})</span>
                            )}
                        </button>
                    </div>

                    {filteredPhysicalOrders.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-2xl border border-dashed text-xs space-y-1">
                            <span className="material-icons text-3xl text-gray-300">storefront</span>
                            <p className="font-semibold text-gray-600">Belum ada pesanan pada kategori ini</p>
                            <p className="text-[11px] text-gray-400">Silakan pilih status lain atau mulai berbelanja di Toko</p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {paginatedPhysical.map(order => {
                                    const status = (order.status || '').toLowerCase();
                                    let badgeStyle = 'bg-gray-50 text-gray-500 border border-gray-200';
                                    let statusLabel = order.status || 'Pending';
                                    let showPayAction = false;
                                    let showReviewPrompt = false;

                                    if (['paid', 'berhasil', 'success'].includes(status)) {
                                        badgeStyle = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
                                        statusLabel = 'Diproses';
                                    } else if (['shipped', 'dikirim', 'dalam pengiriman'].includes(status)) {
                                        badgeStyle = 'bg-purple-50 text-purple-700 border border-purple-200 animate-pulse';
                                        statusLabel = 'Dalam Pengiriman';
                                    } else if (['pending', 'menunggu', 'unpaid'].includes(status)) {
                                        badgeStyle = 'bg-amber-50 text-amber-700 border border-amber-200';
                                        statusLabel = 'Menunggu Bayar';
                                        showPayAction = true;
                                    } else if (['selesai', 'completed', 'delivered'].includes(status)) {
                                        badgeStyle = 'bg-teal-50 text-teal-700 border border-teal-200';
                                        statusLabel = 'Selesai';
                                        showReviewPrompt = true;
                                    } else if (['failed', 'gagal', 'batal', 'cancelled'].includes(status)) {
                                        badgeStyle = 'bg-red-50 text-red-600 border border-red-200';
                                        statusLabel = 'Batal';
                                    }

                                    const orderTotal = Number(order.grand_total) > 0 ? Number(order.grand_total) : (Number(order.total_price) || 0);

                                    return (
                                        <div key={order.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h3 className="text-sm font-black text-gray-900 tracking-tight">Order #{order.order_number}</h3>
                                                    {order.shipping_courier && (
                                                        <p className="text-[10px] text-gray-400 font-semibold uppercase mt-0.5">Kurir: {order.shipping_courier}</p>
                                                    )}
                                                </div>
                                                <span className={`text-[10px] px-2.5 py-1 rounded-full font-black uppercase tracking-widest ${badgeStyle}`}>
                                                    {statusLabel}
                                                </span>
                                            </div>
                                            <div className="space-y-1.5 my-3">
                                                {order.items?.map((item, idx) => (
                                                    <div key={idx} className="text-[11px] text-gray-600 flex justify-between items-center bg-gray-50/70 p-2.5 rounded-xl">
                                                        <span className="font-bold">{item.product_name} <span className="text-gray-400 font-medium">x{item.quantity}</span></span>
                                                        <span className="font-black text-gray-500">Rp {formatIDR(Number(item.price) || 0)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex justify-between items-center pt-3 border-t border-gray-50 mt-3">
                                                <div className="flex items-center gap-1.5 text-gray-400">
                                                    <span className="material-icons text-xs">calendar_today</span>
                                                    <span className="text-[10px] font-bold">{formatDate(order.created_at)}</span>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Grand Total</p>
                                                    <p className="text-sm font-black text-emerald-600">
                                                        Rp {formatIDR(orderTotal)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-4 flex items-center justify-end gap-2">
                                                {showPayAction && (
                                                    <Link 
                                                        to={`/pembayaran/${order.order_number}`} 
                                                        state={{ 
                                                            orderId: order.id, 
                                                            orderNumber: order.order_number, 
                                                            amount: orderTotal, 
                                                            isRepayment: true,
                                                            addUniqueCode: false,
                                                            bank: 'qris',
                                                            cartItems: (order.items || []).map(it => ({
                                                                product: {
                                                                    title: it.product_name,
                                                                    price: it.price,
                                                                    thumbnail: it.product_thumbnail || it.product_image
                                                                },
                                                                quantity: it.quantity,
                                                                price: it.price
                                                            }))
                                                        }} 
                                                        className="text-[10px] bg-amber-500 hover:bg-amber-600 text-white px-3.5 py-2 rounded-xl font-black uppercase tracking-wider transition flex items-center gap-1 shadow-sm"
                                                    >
                                                        <span className="material-icons text-xs">payment</span>
                                                        Bayar Sekarang
                                                    </Link>
                                                )}
                                                <Link to="/riwayat-belanja" className="text-[10px] bg-gray-900 text-white px-4 py-2 rounded-xl font-black uppercase tracking-widest hover:bg-emerald-600 transition-colors flex items-center gap-2">
                                                    Detail Lacak <span className="material-icons text-xs">arrow_forward</span>
                                                </Link>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <Pagination 
                                currentPage={sinergyPage}
                                totalItems={filteredPhysicalOrders.length}
                                itemsPerPage={ITEMS_PER_PAGE}
                                onPageChange={setSinergyPage}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const DonationsTab = () => {
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        const fetchDonations = async () => {
            const user = JSON.parse(localStorage.getItem('user'));
            if (!user) return;
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/donations/donation/`, {
                    headers: { Authorization: `Bearer ${user.access}` }
                });
                const sorted = (res.data || []).sort((a, b) => new Date(b.created_at || b.id || 0) - new Date(a.created_at || a.id || 0));
                setDonations(sorted);
            } catch (err) {
                console.error('Error fetching donations:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchDonations();
    }, []);

    if (loading) return (
        <div className="space-y-3 animate-pulse">
            <div className="p-4 bg-gray-50 rounded-xl mb-4">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                <div className="h-10 bg-gray-200 rounded w-full"></div>
            </div>
            {[1, 2].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl"></div>)}
        </div>
    );

    const paginatedDonations = donations.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex justify-between items-center px-1">
                <h2 className="font-bold text-gray-800">Riwayat Donasi</h2>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-bold">
                        {donations.length} Donasi
                    </span>
                    <Link to="/charity" className="text-[10px] bg-green-600 text-white px-3 py-1 rounded-full font-black uppercase tracking-wider hover:bg-green-700 transition-colors">Donasi Lagi</Link>
                </div>
            </div>
            {donations.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                    <span className="material-icons text-4xl mb-2 opacity-20">volunteer_activism</span>
                    <p className="text-sm font-medium">Belum ada riwayat donasi</p>
                </div>
            ) : (
                <>
                    <div className="space-y-3">
                        {paginatedDonations.map(item => (
                            <div key={item.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex items-center gap-4">
                                <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 border border-gray-50 bg-gray-50">
                                    <img
                                        src={item.campaign?.thumbnail || '/images/default-campaign.jpg'}
                                        alt="Campaign"
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.src = '/images/default-campaign.jpg'; }}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-bold text-gray-800 truncate leading-tight">{item.campaign_title || 'Donasi Tanpa Judul'}</h3>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className="text-[11px] text-emerald-600 font-black">Rp {formatIDR(item.amount)}</span>
                                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{formatDate(item.created_at)}</span>
                                    </div>
                                    {item.message && (
                                        <p className="text-xs text-gray-600 italic mt-1.5 bg-emerald-50/60 p-2 rounded-xl border border-emerald-100/60">
                                            💬 "{item.message}"
                                        </p>
                                    )}
                                </div>
                                <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center">
                                    <span className="material-icons text-green-500 text-lg">check_circle</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Pagination 
                        currentPage={currentPage}
                        totalItems={donations.length}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </div>
    );
};

const BusinessesTab = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSubTab, setActiveSubTab] = useState(0);

    useEffect(() => {
        const fetchBusinesses = async () => {
            try {
                const res = await businessProfileService.getBusinessProfiles();
                const sorted = (res.data || []).sort((a, b) => new Date(b.created_at || b.id || 0) - new Date(a.created_at || a.id || 0));
                setBusinesses(sorted);
            } catch (err) {
                console.error('Error fetching businesses:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchBusinesses();
    }, []);

    if (loading) return (
        <div className="space-y-4 animate-pulse">
            <div className="h-10 bg-gray-200 rounded-xl w-full mb-4"></div>
            <div className="h-64 bg-gray-100 rounded-3xl w-full"></div>
        </div>
    );

    if (businesses.length === 0) return (
        <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
            <span className="material-icons text-4xl mb-2 opacity-20">storefront</span>
            <p className="text-sm font-medium">Belum ada data usaha terdaftar</p>
            <Link to="/dashboard/business-data/new" className="text-xs text-green-600 font-bold mt-2 inline-block hover:underline">
                Daftarkan Usaha Sekarang
            </Link>
        </div>
    );

    const biz = businesses[activeSubTab];

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Sub-tabs for multiple businesses */}
            {businesses.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-4 p-1.5 bg-gray-50 rounded-2xl">
                    {businesses.map((b, idx) => (
                        <button
                            key={b.id}
                            onClick={() => setActiveSubTab(idx)}
                            className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${activeSubTab === idx ? 'bg-white text-green-700 shadow-sm' : 'text-gray-400'}`}
                        >
                            USAHA {idx + 1} ({b.brand_name})
                        </button>
                    ))}
                </div>
            )}

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="h-48 relative">
                    <img src={biz.foto_produk_1 || biz.logo} alt="Header" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-6 right-6 flex items-end gap-4">
                        <div className="w-16 h-16 rounded-2xl border-2 border-white overflow-hidden bg-white shadow-lg shrink-0">
                            <img src={biz.logo} alt="Logo" className="w-full h-full object-cover" />
                        </div>
                        <div className="pb-1">
                            <h3 className="text-xl font-black text-white leading-tight">{biz.brand_name}</h3>
                            <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest">{biz.business_field_display}</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ProfileInfoItem label="Tagline" value={biz.tagline} icon="campaign" fullWidth />
                        <ProfileInfoItem label="Status Usaha" value={biz.business_status_display} icon="analytics" />
                        <ProfileInfoItem label="Area Penjualan" value={biz.sales_area_display} icon="public" />
                        <ProfileInfoItem label="Kesiapan Order" value={biz.readiness_order_display} icon="task_alt" />
                        <ProfileInfoItem label="Kontak Display" value={biz.display_contact} icon="contact_phone" />
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">Deskripsi & Produk</h4>
                        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                            <p className="text-sm text-gray-600 leading-relaxed mb-4">{biz.description}</p>
                            <div className="pt-4 border-t border-gray-200">
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Produk Utama</label>
                                <p className="text-sm font-bold text-gray-800">{biz.main_products}</p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">Keunggulan & Target</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-5 bg-green-50/50 rounded-2xl border border-green-100">
                                <label className="block text-[10px] font-black text-green-700 uppercase tracking-widest mb-2">Keunggulan</label>
                                <p className="text-xs font-medium text-green-800 leading-relaxed">{biz.keunggulan}</p>
                            </div>
                            <div className="p-5 bg-blue-50/50 rounded-2xl border border-blue-100">
                                <label className="block text-[10px] font-black text-blue-700 uppercase tracking-widest mb-2">Target Market</label>
                                <p className="text-xs font-medium text-blue-800 leading-relaxed">{biz.target_market}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {biz.foto_produk_1 && (
                            <div className="aspect-square rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                <img src={biz.foto_produk_1} className="w-full h-full object-cover" />
                            </div>
                        )}
                        {biz.foto_produk_2 && (
                            <div className="aspect-square rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                <img src={biz.foto_produk_2} className="w-full h-full object-cover" />
                            </div>
                        )}
                        {biz.foto_produk_3 && (
                            <div className="aspect-square rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                <img src={biz.foto_produk_3} className="w-full h-full object-cover" />
                            </div>
                        )}
                    </div>
                    
                    <div className="pt-4 flex justify-end">
                        <Link to={`/dashboard/business-data/edit/${biz.id}`} className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-green-700 transition shadow-lg shadow-gray-200">
                            <span className="material-icons text-sm">edit</span> Edit Data Usaha
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};


const ShoppingTab = () => {
    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <h2 className="font-bold text-gray-800 px-1">Belanja</h2>
            <div className="grid grid-cols-1 gap-4">
                <Link to="/keranjang" className="bg-gradient-to-br from-emerald-500 to-teal-600 p-5 rounded-3xl text-white shadow-xl shadow-emerald-100 flex flex-col gap-4 transition-all hover:-translate-y-1 active:scale-[0.98]">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                        <span className="material-icons text-2xl">shopping_cart</span>
                    </div>
                    <div>
                        <h3 className="font-black text-sm uppercase tracking-wider">Keranjang Belanja</h3>
                        <p className="text-[10px] opacity-80 font-medium mt-1 leading-relaxed">Selesaikan belanjaan yang masih tersimpan</p>
                    </div>
                    <div className="mt-2 flex justify-end">
                        <span className="material-icons text-sm">arrow_forward</span>
                    </div>
                </Link>
            </div>

            <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100 mt-8">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 px-1">Butuh Bantuan Belanja?</h4>
                <div className="space-y-3">
                    <a href="https://wa.me/6281234567890" target="_blank" rel="noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 hover:border-green-300 transition-colors group">
                        <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors">
                            <svg className="w-5 h-5 fill-current text-green-500 group-hover:text-white transition-colors" viewBox="0 0 24 24">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.888-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.347-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-xs font-black text-gray-700">Chat Admin Support</p>
                            <p className="text-[10px] text-gray-400 font-medium">Bantuan cepat via WhatsApp</p>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    );
};

const ProfilePage = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState({
        name_full: '',
        nickname: '',
        gender: '',
        birth_date: '',
        birth_place: '',
        marital_status: '',
        segment: '',
        study_level: '',
        study_campus: '',
        study_faculty: '',
        study_department: '',
        study_program: '',
        study_semester: '',
        study_start_year: '',
        study_finish_year: '',
        address: '',
        job: '',
        work_field: '',
        work_institution: '',
        work_position: '',
        work_salary: '',
        address_latitude: '',
        address_longitude: '',
        address_province: '',
        picture: '', // Profile picture URL
        username: '',
        email: '',
        phone: '',
        has_usable_password: true,
    });

    const [activeTab, setActiveTab] = useState('general');
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [showPwaGuideModal, setShowPwaGuideModal] = useState(false);
    // Change password state
    const [showChangePwModal, setShowChangePwModal] = useState(false);

    const [changePwForm, setChangePwForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
    const [changePwLoading, setChangePwLoading] = useState(false);
    const [changePwError, setChangePwError] = useState('');
    const [sendTempPwLoading, setSendTempPwLoading] = useState(false);
    const [sendTempPwMessage, setSendTempPwMessage] = useState('');
    const [sendTempPwError, setSendTempPwError] = useState('');

    const [walletData, setWalletData] = useState({ balance: 0, transactions: [] });
    const [loadingWallet, setLoadingWallet] = useState(true);
    const [showWalletModal, setShowWalletModal] = useState(false);

    // E-Wallet Withdrawal State
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [donationAmount, setDonationAmount] = useState('');
    const [bankName, setBankName] = useState('');
    const [manualBankName, setManualBankName] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [withdrawing, setWithdrawing] = useState(false);
    const [withdrawError, setWithdrawError] = useState('');

    const BANK_OPTIONS = [
        'BSI', 'BCA', 'Mandiri', 'BRI', 'BNI', 'Permata', 'CIMB Niaga',
        'Muamalat', 'Danamon', 'Seabank', 'Bank Jago', 'GOPAY', 'OVO',
        'DANA', 'ShopeePay', 'Lainnya'
    ];

    const currentBank = (bankName === 'Lainnya' ? manualBankName : bankName).toUpperCase();
    const adminFeeVal = (currentBank === 'BSI' || currentBank === 'GOPAY' || !bankName) ? 0 : 6500;
    const totalDeductionVal = (parseFloat(withdrawAmount) || 0) + (parseFloat(donationAmount) || 0) + adminFeeVal;

    const handleTarikSemua = () => {
        const availableBal = Number(walletData.balance || 0);
        const maxWithdraw = Math.max(0, availableBal - adminFeeVal);
        setWithdrawAmount(maxWithdraw.toString());
    };

    const handleWalletWithdraw = async (e) => {
        e.preventDefault();
        setWithdrawError('');
        const amount = parseFloat(withdrawAmount);
        const donation = parseFloat(donationAmount || 0);

        if (isNaN(amount) || amount <= 0) {
            setWithdrawError('Nominal penarikan harus lebih dari 0.');
            return;
        }

        if (totalDeductionVal > Number(walletData.balance || 0)) {
            setWithdrawError('Saldo BAE tidak mencukupi (termasuk biaya admin jika ada).');
            return;
        }

        if (!accountName.trim() || !accountNumber.trim()) {
            setWithdrawError('Nomor rekening dan nama pemilik rekening wajib diisi.');
            return;
        }

        setWithdrawing(true);
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/digital-products/withdrawals/`, {
                amount,
                donation_amount: donation,
                bank_name: bankName === 'Lainnya' ? manualBankName : bankName,
                account_name: accountName,
                account_number: accountNumber,
                withdrawal_source: 'user_wallet'
            }, {
                headers: { Authorization: `Bearer ${user.access}` }
            });

            alert('Permintaan penarikan Saldo BAE berhasil diajukan! Dana akan diproses dan ditransfer maksimal 2x24 jam.');
            setShowWithdrawModal(false);
            setWithdrawAmount('');
            setDonationAmount('');
            setBankName('');
            setManualBankName('');
            setAccountName('');
            setAccountNumber('');
            fetchWallet();
        } catch (err) {
            console.error(err);
            setWithdrawError(err.response?.data?.error || 'Gagal mengajukan penarikan Saldo BAE');
        } finally {
            setWithdrawing(false);
        }
    };

    const fetchWallet = async () => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            if (!user) return;
            const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/transactions/history/`, {
                headers: { Authorization: `Bearer ${user.access}` }
            });
            setWalletData(res.data);
        } catch (error) {
            console.error('Failed to fetch wallet:', error);
        } finally {
            setLoadingWallet(false);
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user && user.id) {
                    const profileData = await authService.getProfile(user.id); // Fetch profile data
                    setProfile(profileData);
                    fetchWallet();
                } else {
                    navigate('/login');
                }
            } catch (error) {
                console.error('Failed to fetch profile:', error);
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setChangePwError('');
        if (changePwForm.new_password !== changePwForm.confirm_password) {
            setChangePwError('Konfirmasi password tidak cocok.');
            return;
        }
        if (changePwForm.new_password.length < 8) {
            setChangePwError('Password baru minimal 8 karakter.');
            return;
        }
        setChangePwLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/change-password/`,
                changePwForm,
                { headers: { Authorization: `Bearer ${user.access}`, 'Content-Type': 'application/json' } }
            );
            alert('Password berhasil diubah! Silakan login kembali.');
            setShowChangePwModal(false);
            setChangePwForm({ old_password: '', new_password: '', confirm_password: '' });
            // Logout setelah ganti password karena token lama sudah tidak valid
            localStorage.removeItem('user');
            window.location.href = '/login';
        } catch (err) {
            setChangePwError(err.response?.data?.error || 'Gagal mengubah password.');
        } finally {
            setChangePwLoading(false);
        }
    };
    
    const handleSendTempPasswordWA = async () => {
        setSendTempPwLoading(true);
        setSendTempPwMessage('');
        setSendTempPwError('');
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-temp-password-wa/`,
                {},
                { headers: { Authorization: `Bearer ${user.access}` } }
            );
            setSendTempPwMessage(res.data.message || 'Password sementara berhasil dikirim ke nomor WhatsApp Anda.');
        } catch (err) {
            setSendTempPwError(err.response?.data?.error || 'Gagal mengirim password sementara.');
        } finally {
            setSendTempPwLoading(false);
        }
    };

    // Render tab content
    const renderTabContent = () => {
        switch (activeTab) {
            case 'general':
                return (
                    <div className="space-y-4">
                        {/* Full Name */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Nama Lengkap</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.name_full || '-'}</p>
                        </div>

                        {/* Gender */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Jenis Kelamin</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">
                                {GENDER_CHOICES[profile.gender] || '-'}
                            </p>
                        </div>

                        {/* Agama */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Agama</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">
                                {AGAMA_CHOICES[profile.agama] || '-'}
                            </p>
                        </div>

                        {/* Birth Date */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Tanggal Lahir</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{formatDate(profile.birth_date) || '-'}</p>
                        </div>

                        {/* Birth Place */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Tempat Lahir</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.birth_place || '-'}</p>
                        </div>

                        {/* Marital Status */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Status Pernikahan</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">
                                {MARITAL_CHOICES[profile.marital_status] || '-'}
                            </p>
                        </div>

                        {/* Segment */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Segment</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{SEGMENT_CHOICES[profile.segment] || '-'}</p>
                        </div>
                    </div>
                );

            case 'address':
                return (
                    <div className="space-y-4">
                        {/* Address */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Alamat</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.address || '-'}</p>
                        </div>

                        {/* Address Latitude */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Latitude</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.address_latitude || '-'}</p>
                        </div>

                        {/* Address Longitude */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Longitude</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.address_longitude || '-'}</p>
                        </div>

                        {/* Address Province */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Provinsi</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{PROVINCE_CHOICES[profile.address_province] || '-'}</p>
                        </div>
                    </div>
                );

            case 'study':
                return (
                    <div className="space-y-4">
                        {/* Study Level */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Tingkat Pendidikan</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{STUDY_LEVEL_CHOICES[profile.study_level] || '-'}</p>
                        </div>

                        {/* Study Campus */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Kampus</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_campus || '-'}</p>
                        </div>

                        {/* Study Faculty */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Fakultas</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_faculty || '-'}</p>
                        </div>

                        {/* Study Department */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Jurusan</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_department || '-'}</p>
                        </div>

                        {/* Study Program */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Program Studi</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_program || '-'}</p>
                        </div>

                        {/* Study Semester */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Semester</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_semester || '-'}</p>
                        </div>

                        {/* Study Start Year */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Tahun Mulai</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_start_year || '-'}</p>
                        </div>

                        {/* Study Finish Year */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Tahun Selesai</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.study_finish_year || '-'}</p>
                        </div>
                    </div>
                );

            case 'work':
                return (
                    <div className="space-y-4">
                        {/* Job */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Pekerjaan</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{JOB_CHOICES[profile.job] || '-'}</p>
                        </div>

                        {/* Work Field */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Bidang Pekerjaan</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{WORK_FIELD_CHOICES[profile.work_field] || '-'}</p>
                        </div>

                        {/* Work Institution */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Institusi</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.work_institution || '-'}</p>
                        </div>

                        {/* Work Position */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Posisi</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">{profile.work_position || '-'}</p>
                        </div>

                        {/* Work Salary */}
                        <div className="w-full">
                            <label className="block text-gray-700 font-medium mb-1">Gaji</label>
                            <p className="w-full p-2 border rounded-lg bg-gray-100">Rp. {formatIDR(profile.work_salary) || '-'}</p>
                        </div>
                    </div>
                );

            case 'courses':
                return <CoursesTab />;

            case 'purchases':
                return <PurchasesTab />;

            default:
                return null;
        }
    };

    return (
        <div className="body">
            <Helmet>
                <meta name="description" content="Lengkapi data anggota, bantu kembangkan aplikasi menjadi lebih Barakah" />
                <meta property="og:title" content="BARAKAH APP" />
                <meta property="og:description" content="Lengkapi data anggota, bantu kembangkan aplikasi menjadi lebih Barakah" />
                <meta property="og:image" content="%PUBLIC_URL%/images/web-thumbnail.jpg" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
            </Helmet>

            <Header />
            <div className="max-w-6xl mx-auto px-4">
                {loadingProfile ? (
                    <div className="bg-white rounded-lg shadow overflow-hidden mt-6 animate-pulse p-4 mb-20">
                        <div className="h-6 bg-gray-200 rounded w-24 mb-4"></div>
                        <div className="flex flex-col items-center space-y-4">
                            <div className="w-32 h-32 rounded-full bg-gray-200 border-2 border-gray-100"></div>
                            <div className="w-full flex gap-2 border-b pb-2">
                                {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="w-12 h-8 bg-gray-200 rounded"></div>)}
                            </div>
                            <div className="w-full space-y-4 mt-4">
                                {[1, 2, 3, 4, 5, 6].map(i => (
                                    <div key={i} className="w-full">
                                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-1"></div>
                                        <div className="h-10 bg-gray-100 border rounded-lg w-full"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mt-6 pb-24 max-w-4xl mx-auto">
                        {/* Header Banner */}
                        <div className="h-32 bg-gradient-to-r from-green-600 to-emerald-700 relative">
                            <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 px-1 py-1 bg-white rounded-full shadow-lg">
                                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white">
                                    <img
                                        src={profile.picture ? (profile.picture.startsWith('http') ? profile.picture : `${process.env.REACT_APP_API_BASE_URL}${profile.picture}`) : `${process.env.REACT_APP_API_BASE_URL}/media/profile_images/pas_foto_standard.png`}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-20 px-6">
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-black text-gray-900">{profile.nickname || profile.name_full || profile.username || 'Anggota Barakah'}</h3>
                                {profile.nickname && profile.name_full && <p className="text-xs text-gray-400 font-bold -mt-1">{profile.name_full}</p>}
                                {profile.position && (
                                    <div className="mt-1">
                                        <span className="text-sm font-bold text-green-700 bg-green-50 px-3 py-1 rounded-lg border border-green-100">
                                            {profile.position}
                                        </span>
                                    </div>
                                )}
                                <p className="text-sm text-gray-500 font-medium mt-1">{profile.email || 'tanpa email'}</p>
                                {profile.phone && <p className="text-xs text-emerald-600 font-black mt-1 flex items-center justify-center gap-1"><span className="material-icons text-xs">phone</span> {profile.phone}</p>}
                                <div className="flex justify-center flex-wrap gap-2 mt-3">
                                    {profile.is_verified_member && (
                                        <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 border border-blue-100">
                                            <span className="material-icons text-xs">verified</span>
                                            Anggota Terverifikasi
                                        </span>
                                    )}
                                    <span className="bg-green-50 text-green-600 text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 border border-green-100 uppercase">
                                        {SEGMENT_CHOICES[profile.segment] || 'Umum'}
                                    </span>
                                    {profile.labels && profile.labels.map((lbl, idx) => (
                                        <span key={idx} className="bg-purple-50 text-purple-600 text-[10px] font-bold px-3 py-1 rounded-full border border-purple-100 uppercase tracking-tighter">
                                            {lbl}
                                        </span>
                                    ))}
                                </div>

                                {/* Follower, Following & Like Stats */}
                                <div className="flex items-center justify-center gap-6 mt-4 py-2.5 px-6 bg-gray-50 rounded-2xl max-w-sm mx-auto border border-gray-100 shadow-2xs">
                                    <div className="text-center">
                                        <span className="block text-lg font-black text-gray-900">{profile.followers_count || 0}</span>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pengikut</span>
                                    </div>
                                    <div className="h-6 w-[1px] bg-gray-200"></div>
                                    <div className="text-center">
                                        <span className="block text-lg font-black text-gray-900">{profile.following_count || 0}</span>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Mengikuti</span>
                                    </div>
                                    <div className="h-6 w-[1px] bg-gray-200"></div>
                                    <div className="text-center">
                                        <span className="block text-lg font-black text-rose-600">{profile.shop_likes_count || 0}</span>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Suka Toko</span>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Stats/Links */}
                            <div className="grid grid-cols-2 gap-3 mb-6">
                                <Link to="/profile/edit" className="flex items-center justify-center gap-2 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-2xl text-sm font-bold transition">
                                    <span className="material-icons text-sm">edit</span>
                                    Edit Profil
                                </Link>
                                <Link to="/dashboard" className="flex items-center justify-center gap-2 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-2xl text-sm font-bold transition">
                                    <span className="material-icons text-sm">dashboard</span>
                                    Dashboard
                                </Link>
                            </div>

                            {/* Saldo BAE Card */}
                            <div className="mb-8 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 rounded-3xl p-6 text-white shadow-xl shadow-emerald-900/10 relative overflow-hidden">
                                <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1.5 opacity-90">
                                            <span className="material-icons text-emerald-200 text-sm">account_balance_wallet</span>
                                            <span className="text-[11px] font-black tracking-widest uppercase text-emerald-100">Saldo BAE Anda</span>
                                        </div>
                                        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                                            Rp {formatIDR(walletData.balance || 0)}
                                        </h2>
                                        <p className="text-[11px] text-emerald-100/80 mt-1">
                                            Dapat digunakan untuk belanja E-Commerce, Sinergy, atau dicairkan ke rekening Anda.
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-2 pt-2 sm:pt-0">
                                        <button
                                            onClick={() => setShowWalletModal(true)}
                                            className="px-4 py-2.5 bg-white/15 hover:bg-white/25 backdrop-blur-md rounded-2xl text-xs font-black uppercase tracking-wider text-white border border-white/20 transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer"
                                        >
                                            <span className="material-icons text-sm">receipt_long</span>
                                            Mutasi
                                        </button>
                                        <button
                                            onClick={() => {
                                                setWithdrawError('');
                                                setShowWithdrawModal(true);
                                            }}
                                            className="px-5 py-2.5 bg-white text-emerald-900 hover:bg-emerald-50 rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-lg shadow-black/10 active:scale-95 cursor-pointer"
                                        >
                                            <span className="material-icons text-sm">payments</span>
                                            Tarik Dana
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Navigation Tabs */}
                            <div className="flex flex-wrap justify-center md:justify-start gap-1 mb-6 bg-gray-50 p-1.5 rounded-2xl">
                                {[
                                    { id: 'general', icon: 'person', label: 'Data Diri' },
                                    { id: 'address', icon: 'location_on', label: 'Alamat' },
                                    { id: 'study', icon: 'school', label: 'Pendidikan' },
                                    { id: 'work', icon: 'work', label: 'Pekerjaan' },
                                    { id: 'shop', icon: 'storefront', label: 'Toko' },
                                    { id: 'courses', icon: 'school', label: 'Kelas' },
                                    { id: 'charity', icon: 'volunteer_activism', label: 'Donasi' },
                                    { id: 'business', icon: 'handshake', label: 'Bisnis' },
                                    { id: 'purchases', icon: 'history', label: 'Riwayat' }
                                ].map(tab => {
                                    // Check if user is Anggota for business tab
                                    const userRes = JSON.parse(localStorage.getItem('user'));
                                    const isAnggota = userRes?.custom_roles_data?.some(r => r.name.toLowerCase().includes('anggota') || r.code.toLowerCase().includes('anggota')) || userRes?.role === 'admin';
                                    if (tab.id === 'business' && !isAnggota) return null;
                                    
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap text-sm font-bold transition-all ${activeTab === tab.id
                                                    ? 'bg-white text-green-700 shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-600'
                                                }`}
                                        >
                                            <span className="material-icons text-lg">{tab.icon}</span>
                                            {tab.label}
                                        </button>
                                    );
                                })}

                            </div>

                            {/* Tab Content Area */}
                            <div className="min-h-[300px]">
                                {activeTab === 'general' && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">Informasi Pribadi</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <ProfileInfoItem label="Nama Panggilan" value={profile.nickname} icon="face" />
                                            <ProfileInfoItem label="Nama Lengkap" value={profile.name_full} icon="badge" />
                                            <ProfileInfoItem label="NIK / No. KTP" value={profile.nik} icon="fingerprint" />
                                            <ProfileInfoItem label="Jenis Kelamin" value={GENDER_CHOICES[profile.gender]} icon="wc" />
                                            <ProfileInfoItem label="Agama" value={AGAMA_CHOICES[profile.agama] || profile.agama} icon="self_improvement" />
                                            <ProfileInfoItem label="Tgl Lahir" value={formatDate(profile.birth_date)} icon="calendar_today" />
                                            <ProfileInfoItem label="Tempat Lahir" value={profile.birth_place} icon="map" />
                                            <ProfileInfoItem label="Status Pernikahan" value={MARITAL_CHOICES[profile.marital_status]} icon="favorite" />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'address' && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">Lokasi & Pengiriman</h4>
                                        <div className="bg-emerald-50 rounded-2xl p-4 mb-4 border border-emerald-100 flex gap-3">
                                            <span className="material-icons text-emerald-600">info</span>
                                            <p className="text-[11px] text-emerald-800 leading-relaxed font-medium">Data kelurahan digunakan untuk menghitung biaya ongkir dari kurir E-commerce kami.</p>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <ProfileInfoItem label="Provinsi" value={profile.address_province} icon="domain" />
                                            <ProfileInfoItem label="Kota / Kabupaten" value={profile.address_city_name} icon="location_city" />
                                            <ProfileInfoItem label="Kecamatan" value={profile.address_subdistrict_name} icon="home_work" />
                                            <ProfileInfoItem label="Kelurahan / Desa" value={profile.address_village_name} icon="holiday_village" />
                                            <ProfileInfoItem label="RT / RW" value={profile.address_rt_rw} icon="signpost" />
                                            <ProfileInfoItem label="Kode Pos" value={profile.address_postal_code} icon="markunread_mailbox" />
                                            <ProfileInfoItem label="Alamat Lengkap" value={profile.address} icon="place" fullWidth />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'study' && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">Riwayat Pendidikan</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <ProfileInfoItem label="Tingkat" value={STUDY_LEVEL_CHOICES[profile.study_level]} icon="layers" />
                                            <ProfileInfoItem label="Kampus / Sekolah" value={profile.study_campus} icon="account_balance" />
                                            <ProfileInfoItem label="Fakultas" value={profile.study_faculty} icon="domain" />
                                            <ProfileInfoItem label="Jurusan / Prodi" value={profile.study_program || profile.study_department} icon="architecture" />
                                            <ProfileInfoItem label="Semester Current" value={profile.study_semester} icon="numbers" />
                                            <ProfileInfoItem label="Periode" value={profile.study_start_year ? `${profile.study_start_year} - ${profile.study_finish_year || 'Sekarang'}` : null} icon="date_range" />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'work' && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest px-1">Informasi Karir</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <ProfileInfoItem label="Pekerjaan" value={JOB_CHOICES[profile.job]} icon="business_center" />
                                            <ProfileInfoItem label="Bidang" value={WORK_FIELD_CHOICES[profile.work_field]} icon="category" />
                                            <ProfileInfoItem label="Instansi" value={profile.work_institution} icon="apartment" />
                                            <ProfileInfoItem label="Posisi" value={profile.work_position} icon="badge" />
                                            <ProfileInfoItem label="Estimasi Gaji" value={profile.work_salary ? `Rp ${formatIDR(profile.work_salary)}` : null} icon="payments" />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'shop' && (
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center px-1">
                                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Informasi Toko</h4>
                                            <Link to={`/store/${profile.shop_name || profile.username || ''}`} className="text-[10px] font-black text-emerald-700 uppercase flex items-center gap-1 hover:underline">
                                                Kunjungi Toko <span className="material-icons text-xs">open_in_new</span>
                                            </Link>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <ProfileInfoItem label="Nama Toko" value={profile.shop_name ? profile.shop_name : `Belum diatur (@${profile.username})`} icon="storefront" />
                                            <ProfileInfoItem label="Alamat Toko (URL)" value={`https://barakah.cloud/store/${profile.shop_name || profile.username}`} icon="link" />
                                            <ProfileInfoItem label="Deskripsi Toko" value={profile.shop_description} icon="description" fullWidth />
                                            <ProfileInfoItem label="Tema Warna" value={profile.shop_theme_color} icon="palette" />
                                            <ProfileInfoItem label="Gaya Font" value={profile.shop_font} icon="font_download" />
                                            <ProfileInfoItem label="Kurir Aktif" value={profile.shop_supported_couriers?.toUpperCase()} icon="local_shipping" fullWidth />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'courses' && <CoursesTab />}
                                {activeTab === 'purchases' && <PurchasesTab />}
                                {activeTab === 'charity' && <DonationsTab />}
                                {activeTab === 'business' && <BusinessesTab />}
                            </div>

                            {/* Footer Actions */}
                            <div className="mt-12 pt-6 border-t border-gray-100">
                                <button
                                    onClick={() => setShowPwaGuideModal(true)}

                                    className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 py-3.5 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] mb-3 border border-emerald-200/60 shadow-2xs"
                                >
                                    <span className="material-icons text-emerald-600">install_mobile</span>
                                    PASANG APLIKASI (PWA)
                                </button>
                                <button
                                    onClick={() => setShowChangePwModal(true)}
                                    className="w-full bg-blue-50 hover:bg-blue-100 text-blue-600 py-4 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] mb-3"
                                >
                                    <span className="material-icons">lock</span>
                                    GANTI KATA SANDI
                                </button>
                                <button
                                    onClick={() => {
                                        localStorage.removeItem('user');
                                        localStorage.removeItem('access_token');
                                        localStorage.removeItem('refresh_token');
                                        window.location.href = '/login';
                                    }}
                                    className="w-full bg-red-50 hover:bg-red-100 text-red-600 py-4 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                >
                                    <span className="material-icons">logout</span>
                                    KELUAR DARI APLIKASI
                                </button>
                                <p className="text-center text-[10px] text-gray-400 font-bold mt-4 uppercase tracking-widest">Barakah App v2.5 • 2026</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <NavigationButton />
            <PWAInstallGuideModal isOpen={showPwaGuideModal} onClose={() => setShowPwaGuideModal(false)} />


            {/* ======= MODAL GANTI PASSWORD ======= */}
            {showChangePwModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-end justify-center p-4 sm:items-center">
                    <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                            <h2 className="text-lg font-black flex items-center gap-2">
                                <span className="material-icons">lock</span> Ganti Kata Sandi
                            </h2>
                            <p className="text-blue-100 text-xs mt-1">Setelah berhasil, Anda akan diminta login ulang</p>
                        </div>
                        <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                            {changePwError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                                    <p className="text-sm text-red-600">{changePwError}</p>
                                </div>
                            )}
                            {profile.has_usable_password === false && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                    <p className="text-xs text-amber-800 leading-relaxed font-semibold">
                                        Karena Anda masuk menggunakan Google, Anda belum memiliki kata sandi lokal. 
                                        Silakan dapatkan kata sandi sementara terlebih dahulu melalui WhatsApp untuk digunakan sebagai Kata Sandi Lama.
                                    </p>
                                    <button
                                        type="button"
                                        disabled={sendTempPwLoading}
                                        onClick={handleSendTempPasswordWA}
                                        className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 rounded-xl transition text-xs disabled:opacity-60 flex items-center justify-center gap-2"
                                    >
                                        {sendTempPwLoading ? (
                                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                        ) : (
                                            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.888-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.347-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                            </svg>
                                        )}
                                        Kirim Kata Sandi Sementara ke WA
                                    </button>
                                    {sendTempPwMessage && (
                                        <p className="text-xs text-emerald-600 font-bold mt-2">{sendTempPwMessage}</p>
                                    )}
                                    {sendTempPwError && (
                                        <p className="text-xs text-red-600 font-bold mt-2">{sendTempPwError}</p>
                                    )}
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Password Lama</label>
                                <input
                                    type="password"
                                    placeholder="Masukkan password lama"
                                    value={changePwForm.old_password}
                                    onChange={e => setChangePwForm(f => ({ ...f, old_password: e.target.value }))}
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Password Baru</label>
                                <input
                                    type="password"
                                    placeholder="Minimal 8 karakter"
                                    value={changePwForm.new_password}
                                    onChange={e => setChangePwForm(f => ({ ...f, new_password: e.target.value }))}
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Konfirmasi Password Baru</label>
                                <input
                                    type="password"
                                    placeholder="Ulangi password baru"
                                    value={changePwForm.confirm_password}
                                    onChange={e => setChangePwForm(f => ({ ...f, confirm_password: e.target.value }))}
                                    required
                                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition ${changePwForm.confirm_password && changePwForm.new_password !== changePwForm.confirm_password ? 'border-red-300 ring-2 ring-red-200' : 'border-gray-200'}`}
                                />
                                {changePwForm.confirm_password && changePwForm.new_password !== changePwForm.confirm_password && (
                                    <p className="text-xs text-red-500 mt-1">Password tidak cocok</p>
                                )}
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowChangePwModal(false);
                                        setChangePwError('');
                                        setSendTempPwMessage('');
                                        setSendTempPwError('');
                                        setChangePwForm({ old_password: '', new_password: '', confirm_password: '' });
                                    }}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition text-sm"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    disabled={changePwLoading}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {changePwLoading ? (
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                    ) : (
                                        <span className="material-icons text-sm">save</span>
                                    )}
                                    Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Riwayat Mutasi Saldo BAE */}
            {showWalletModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 max-h-[85vh] flex flex-col">
                        <div className="flex justify-between items-center pb-4 border-b border-gray-100">
                            <div>
                                <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                                    <span className="material-icons text-emerald-600">account_balance_wallet</span>
                                    Mutasi Saldo BAE
                                </h3>
                                <p className="text-xs text-gray-400 font-bold mt-0.5">
                                    Saldo Saat Ini: <span className="text-emerald-600 font-black">Rp {formatIDR(walletData.balance || 0)}</span>
                                </p>
                            </div>
                            <button
                                onClick={() => setShowWalletModal(false)}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition"
                            >
                                <span className="material-icons text-base">close</span>
                            </button>
                        </div>

                        <div className="overflow-y-auto flex-1 py-4 space-y-3 pr-1">
                            {(!walletData.transactions || walletData.transactions.length === 0) ? (
                                <div className="text-center py-12 text-gray-400">
                                    <span className="material-icons text-4xl mb-2 opacity-20">receipt_long</span>
                                    <p className="text-sm font-bold">Belum ada riwayat mutasi saldo</p>
                                </div>
                            ) : (
                                walletData.transactions.map(item => {
                                    const isPositive = Number(item.amount) > 0;
                                    return (
                                        <div key={item.id} className="p-3.5 bg-gray-50/80 rounded-2xl border border-gray-100 flex items-start justify-between gap-3 hover:bg-gray-50 transition">
                                            <div className="flex items-start gap-3 min-w-0">
                                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                    <span className="material-icons text-sm">{isPositive ? 'arrow_downward' : 'arrow_upward'}</span>
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-xs font-black text-gray-800 leading-tight">
                                                        {item.transaction_type === 'REFUND' ? 'Pengembalian Dana (Refund)' :
                                                         item.transaction_type === 'PAYMENT' ? 'Pembayaran Belanja' :
                                                         item.transaction_type === 'WITHDRAWAL' ? 'Penarikan Saldo' :
                                                         item.transaction_type === 'EARNING' ? 'Hasil Penjualan' : item.transaction_type}
                                                    </p>
                                                    <p className="text-[11px] text-gray-500 mt-0.5 break-words line-clamp-2">
                                                        {item.description || (item.order_number ? `Order #${item.order_number}` : '-')}
                                                    </p>
                                                    <p className="text-[10px] text-gray-400 mt-1 font-bold">
                                                        {formatDate(item.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className={`text-sm font-black ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    {isPositive ? '+' : ''}Rp {formatIDR(Math.abs(item.amount))}
                                                </p>
                                                <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                                                    Akhir: Rp {formatIDR(item.balance_after)}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="pt-3 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={() => setShowWalletModal(false)}
                                className="px-5 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-gray-800 transition"
                            >
                                Tutup
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Tarik Saldo BAE (E-Wallet Pribadi) */}
            {showWithdrawModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 shadow-2xl flex flex-col max-h-[92vh]">
                        {/* Modal Header */}
                        <div className="p-6 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                                    <span className="material-icons text-xl">account_balance_wallet</span>
                                </div>
                                <div>
                                    <h3 className="font-black text-base">Tarik Saldo BAE</h3>
                                    <p className="text-xs text-emerald-100">Pencairan dana e-wallet pribadi ke rekening</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowWithdrawModal(false)}
                                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition"
                            >
                                <span className="material-icons text-base">close</span>
                            </button>
                        </div>

                        {/* Modal Body / Form */}
                        <div className="p-6 overflow-y-auto space-y-4 flex-1">
                            {/* Saldo Tersedia Banner */}
                            <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100 flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 block">Saldo BAE Tersedia</span>
                                    <span className="text-xl font-black text-emerald-900">Rp {formatIDR(walletData.balance || 0)}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleTarikSemua}
                                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                                >
                                    Tarik Semua
                                </button>
                            </div>

                            {withdrawError && (
                                <div className="p-3 bg-red-50 text-red-700 rounded-xl border border-red-200 text-xs font-medium flex items-center gap-2">
                                    <span className="material-icons text-sm text-red-500">error</span>
                                    {withdrawError}
                                </div>
                            )}

                            <form onSubmit={handleWalletWithdraw} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Nominal Penarikan (Rp) *</label>
                                    <input
                                        type="number"
                                        min="10000"
                                        max={walletData.balance || 0}
                                        value={withdrawAmount}
                                        onChange={(e) => setWithdrawAmount(e.target.value)}
                                        placeholder="Contoh: 100000"
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                                        required
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Minimal penarikan Rp 10.000</p>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Infaq / Sedekah Sukarela (Opsional)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={donationAmount}
                                        onChange={(e) => setDonationAmount(e.target.value)}
                                        placeholder="Nominal infaq (Rp)..."
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Bank / E-Wallet Tujuan *</label>
                                    <select
                                        value={bankName}
                                        onChange={(e) => setBankName(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                                        required
                                    >
                                        <option value="">-- Pilih Bank / E-Wallet --</option>
                                        {BANK_OPTIONS.map(b => (
                                            <option key={b} value={b}>
                                                {b} {b === 'BSI' || b === 'GOPAY' ? '(Gratis Admin)' : '(Admin Rp 6.500)'}
                                            </option>
                                        ))}
                                    </select>
                                    {bankName === 'Lainnya' && (
                                        <input
                                            type="text"
                                            value={manualBankName}
                                            onChange={(e) => setManualBankName(e.target.value)}
                                            placeholder="Tulis nama bank secara manual..."
                                            className="w-full mt-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                                            required
                                        />
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Nomor Rekening / No. HP *</label>
                                        <input
                                            type="text"
                                            value={accountNumber}
                                            onChange={(e) => setAccountNumber(e.target.value)}
                                            placeholder="Contoh: 1234567890"
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Nama Pemilik Rekening *</label>
                                        <input
                                            type="text"
                                            value={accountName}
                                            onChange={(e) => setAccountName(e.target.value)}
                                            placeholder="Sesuai buku tabungan"
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Rincian Biaya */}
                                <div className="bg-amber-50/80 p-4 rounded-2xl border border-amber-100 space-y-1.5 text-xs text-amber-900">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Nominal Penarikan:</span>
                                        <span className="font-bold">Rp {formatIDR(parseFloat(withdrawAmount) || 0)}</span>
                                    </div>
                                    {parseFloat(donationAmount) > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Infaq Sukarela:</span>
                                            <span className="font-bold">Rp {formatIDR(parseFloat(donationAmount) || 0)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Biaya Admin Transfer:</span>
                                        <span className="font-bold">{adminFeeVal === 0 ? 'GRATIS (Rp 0)' : `Rp ${formatIDR(adminFeeVal)}`}</span>
                                    </div>
                                    <div className="flex justify-between pt-1.5 border-t border-amber-200 font-black text-amber-950 text-sm">
                                        <span>Total Potong Saldo:</span>
                                        <span className="text-emerald-800">Rp {formatIDR(totalDeductionVal)}</span>
                                    </div>
                                    <p className="text-[10px] text-amber-700/90 pt-0.5">* Gratis biaya admin transfer jika menggunakan BSI atau GOPAY.</p>
                                </div>

                                {/* 2x24 Jam & WA Support */}
                                <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-100 text-xs text-gray-700 space-y-2">
                                    <p className="text-[11px] font-bold text-gray-800 flex items-center gap-1.5">
                                        <span className="material-icons text-sm text-emerald-600">schedule</span>
                                        Proses pencairan saldo diproses maksimal <strong>2x24 jam</strong> kerja.
                                    </p>
                                    <div className="pt-1.5 border-t border-gray-200/60 flex items-center justify-between">
                                        <span className="text-[10px] text-gray-500">Ada kendala penarikan?</span>
                                        <a
                                            href="https://wa.me/6285643848251?text=Halo%20Admin%20Barakah%20Ekonomi,%20saya%20ingin%20bertanya%20seputar%20penarikan%20saldo%20BAE"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-2.5 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1 transition border border-emerald-200"
                                        >
                                            <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.888-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.347-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                            </svg> Hubungi Admin BAE
                                        </a>
                                    </div>
                                </div>

                                <div className="pt-2 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowWithdrawModal(false)}
                                        className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-black uppercase tracking-wider transition"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={withdrawing || totalDeductionVal > (walletData.balance || 0) || !withdrawAmount || totalDeductionVal <= 0}
                                        className="flex-[2] py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition shadow-lg shadow-emerald-600/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                                    >
                                        {withdrawing ? (
                                            <>
                                                <div className="animate-spin h-3.5 w-3.5 border-b-2 border-white rounded-full"></div>
                                                Memproses...
                                            </>
                                        ) : (
                                            <>
                                                <span className="material-icons text-sm">send</span>
                                                Ajukan Penarikan
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;